import React, { useState, useEffect } from 'react';
import { employeeService } from '../services/employeeService';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import ResetPasswordModal from '../components/ResetPasswordModal';
import './Employees.css';

import DepartmentSelect from '../components/DepartmentSelect';
const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ role: '', status: 'active' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const { user: currentUser, isAdmin } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employee_id: '',
    position: '',
    department: '',
    role: 'employee',
    password: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [filter]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeService.getAll(filter);
      setEmployees(data.employees || []);
    } catch (err) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      employee_id: '',
      position: '',
      department: '',
      role: 'employee',
      password: '',
      is_active: true
    });
  };

  const handleAddEmployee = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleViewEmployee = (emp) => {
    setSelectedEmployee(emp);
    setShowViewModal(true);
  };

  const handleEditEmployee = (emp) => {
    setSelectedEmployee(emp);
    setFormData({
      name: emp.name || '',
      email: emp.email || '',
      phone: emp.phone || '',
      employee_id: emp.employee_id || '',
      position: emp.position || '',
      department: emp.department || '',
      role: emp.role || 'employee',
      password: '',
      is_active: emp.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleDeleteEmployee = (emp) => {
    setSelectedEmployee(emp);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedEmployee) return;

    try {
      setSaving(true);
      await employeeService.delete(selectedEmployee.id);
      toast.success('Employee deleted successfully');
      setShowDeleteModal(false);
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (err) {
      toast.error(err.message || 'Failed to delete employee');
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return false;
    }

    // For drivers, phone is required
    if (formData.role === 'driver' && !formData.phone.trim()) {
      toast.error('Phone number is required for drivers (used for mobile app login)');
      return false;
    }

    // Email OR phone must be provided
    if (!formData.email.trim() && !formData.phone.trim()) {
      toast.error('Either email or phone number is required');
      return false;
    }

    // P2-B (R9): a typed temporary password mirrors the server policy (12–72);
    // blank is fine only when an email can carry an invite — the server rules.
    const pw = formData.password || '';
    if (pw.length > 0 && (pw.length < 12 || pw.length > 72)) {
      toast.error('Temporary password must be between 12 and 72 characters');
      return false;
    }
    if (pw.length === 0 && !formData.email.trim() && !selectedEmployee) {
      toast.error('Set a temporary password — this employee has no email for an invite.');
      return false;
    }

    return true;
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);
      const created = await employeeService.create(formData);
      // P2-B (R10): the server says whether an invite went out; a failed send
      // means "use Reset password" — surface its message verbatim.
      if (created && created.invite_sent === false) toast.warn(created.message, { autoClose: 10000 });
      else if (created && created.invite_sent === true) toast.success('Employee added — invite email sent');
      else toast.success('Employee added successfully');
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      // P2-A (R13): employeeService uses the fetch client, which throws
      // Error(<validator msg | server message | HTTP n>) — err.response never existed here.
      toast.error(err.message || 'Failed to add employee');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSaving(true);
      // F1/F2: the edit PUT never carries a password — password changes go
      // through the "Reset password" action (the admin reset route).
      const { password, ...editData } = formData;
      await employeeService.update(selectedEmployee.id, editData);
      toast.success('Employee updated successfully');
      setShowEditModal(false);
      setSelectedEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (err) {
      toast.error(err.message || 'Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Employees</h1>
        <button className="btn-primary" onClick={handleAddEmployee}>+ Add Employee</button>
      </div>

      <div className="filters">
        <select 
          value={filter.role} 
          onChange={(e) => setFilter({...filter, role: e.target.value})}
        >
          <option value="">All Roles</option>
          <option value="admin">Admins</option>
          <option value="manager">Managers</option>
          <option value="dispatcher">Dispatchers</option>
          <option value="driver">Drivers</option>
          <option value="employee">Employees</option>
        </select>
        <select 
          value={filter.status} 
          onChange={(e) => setFilter({...filter, status: e.target.value})}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading employees...</div>
      ) : employees.length === 0 ? (
        <div className="empty-state">
          <p>No employees found</p>
          <p className="hint">Add your first employee to get started</p>
        </div>
      ) : (
        <div className="table-container" style={{ boxShadow: '0 0 15px rgba(212,169,78,0.12), 0 0 30px rgba(212,169,78,0.06)' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Position</th>
                <th>Department</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.id}>
                  <td>{emp.name}</td>
                  <td>{emp.email || '-'}</td>
                  <td>
                    <span className={`role-badge role-${emp.role || 'employee'}`}>
                      {emp.role || 'employee'}
                    </span>
                  </td>
                  <td>{emp.position || '-'}</td>
                  <td>{emp.department || '-'}</td>
                  <td>{emp.phone || '-'}</td>
                  <td>
                    <span className={emp.is_active ? 'status-active' : 'status-inactive'}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon view" title="View" onClick={() => handleViewEmployee(emp)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      </button>
                      <button className="btn-icon edit" title="Edit" onClick={() => handleEditEmployee(emp)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      {isAdmin && (
                        <button className="btn-icon edit" title="Reset password" onClick={() => setResetTarget(emp)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>
                        </button>
                      )}
                      <button className="btn-icon dispose" title="Delete" onClick={() => handleDeleteEmployee(emp)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>➕ Add New Employee</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitAdd}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Email {formData.role === 'driver' && <span style={{color: '#6B6B6B', fontWeight: 'normal'}}>(Optional for drivers)</span>}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="email@example.com"
                    />
                    {formData.role !== 'driver' && !formData.phone && (
                      <small className="form-hint" style={{color: '#D4A94E'}}>Required if no phone number</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>
                      Phone {formData.role === 'driver' && <span style={{color: '#F87171'}}>*</span>}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 555-5555"
                      required={formData.role === 'driver'}
                    />
                    {formData.role === 'driver' && (
                      <small className="form-hint" style={{color: '#3B82F6'}}>📱 Drivers login with phone number</small>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Role</label>
                    <select name="role" value={formData.role} onChange={handleInputChange}>
                      <option value="employee">Employee</option>
                      <option value="driver">Driver</option>
                      <option value="dispatcher">Dispatcher</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Temporary password (leave blank to email an invite)</label>
                    <input
                      type="text"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="12–72 characters, or leave blank"
                      autoComplete="off"
                    />
                    <small className="form-hint">Blank + email = the employee gets an invite link. No email = a temporary password is required.</small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Position</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder="Job title"
                    />
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <DepartmentSelect
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Employee ID</label>
                    <input
                      type="text"
                      name="employee_id"
                      value={formData.employee_id}
                      onChange={handleInputChange}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group checkbox-group" style={{ alignSelf: 'flex-end', paddingBottom: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Active Employee
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Employee Modal */}
      {showViewModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>👤 Employee Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="employee-details">
                <div className="detail-row">
                  <span className="label">Name:</span>
                  <span className="value">{selectedEmployee.name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Email:</span>
                  <span className="value">{selectedEmployee.email || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Phone:</span>
                  <span className="value">{selectedEmployee.phone || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Employee ID:</span>
                  <span className="value">{selectedEmployee.employee_id || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Role:</span>
                  <span className={`value role-badge role-${selectedEmployee.role || 'employee'}`}>
                    {selectedEmployee.role || 'employee'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Position:</span>
                  <span className="value">{selectedEmployee.position || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Department:</span>
                  <span className="value">{selectedEmployee.department || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className={`value ${selectedEmployee.is_active ? 'status-active' : 'status-inactive'}`}>
                    {selectedEmployee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Certifications:</span>
                  <span className="value">{selectedEmployee.certification_count || 0}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
              <button className="btn-primary" onClick={() => { setShowViewModal(false); handleEditEmployee(selectedEmployee); }}>
                Edit Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Edit Employee</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmitEdit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Email {formData.role === 'driver' && <span style={{color: '#6B6B6B', fontWeight: 'normal'}}>(Optional for drivers)</span>}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Phone {formData.role === 'driver' && <span style={{color: '#F87171'}}>*</span>}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 555-5555"
                      required={formData.role === 'driver'}
                    />
                    {formData.role === 'driver' && (
                      <small className="form-hint" style={{color: '#3B82F6'}}>📱 Drivers login with phone number</small>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Employee ID</label>
                    <input
                      type="text"
                      name="employee_id"
                      value={formData.employee_id}
                      onChange={handleInputChange}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select name="role" value={formData.role} onChange={handleInputChange}>
                      <option value="employee">Employee</option>
                      <option value="driver">Driver</option>
                      <option value="dispatcher">Dispatcher</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Position</label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      placeholder="Job title"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Department</label>
                    <DepartmentSelect
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group checkbox-group" style={{ alignSelf: 'flex-end', paddingBottom: '10px' }}>
                    <label>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Active Employee
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedEmployee && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🗑️ Delete Employee</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{selectedEmployee.name}</strong>?</p>
              <p style={{color: '#F87171', marginTop: '10px'}}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" style={{background: 'rgba(239,68,68,0.15)', color: '#F87171', border: '1px solid rgba(239,68,68,0.3)'}} onClick={confirmDelete} disabled={saving}>
                {saving ? 'Deleting...' : 'Delete Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          isSelf={resetTarget.id === currentUser?.id}
          onClose={() => setResetTarget(null)}
        />
      )}
    </div>
  );
};

export default Employees;
